/**
 * ResumeView page — side-by-side: job requirements vs. generated resume.
 *
 * Layout:
 *  Left panel  — job posting requirements (must-haves, nice-to-haves, responsibilities)
 *  Right panel — generated resume sections (summary, experience bullets, skills)
 *
 * Per bullet: hover tooltip shows the LLM's reasoning for why it was selected.
 * Confidence summary (strengths / gaps / overall score) shown at the top.
 * Bullets support drag-and-drop reordering and XYZ/CAR rewrite suggestions.
 * Collapsed tenures merge all title bullets into one entry with full tenure date span.
 */
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Loader2,
  ExternalLink,
  Info,
  CheckCircle2,
  AlertTriangle,
  Download,
  ChevronDown,
  Pencil,
  Trash2,
  Sparkles,
  Check,
  X,
  ListChecks,
  Crosshair,
} from 'lucide-react';
import { resumeApi, authApi, jobPostingApi } from '@/lib/api';
import type { Resume, ExportFormat } from '@/lib/api';
import type {
  ResumeDocument,
  ResumeExperienceItem,
  ResumeProjectItem,
  ResumeBullet,
  ResumeBulletRow,
  AvailableCapability,
  IcpScoringRow,
  PerspectiveBriefings,
  ViewPreferences,
} from '@resume-builder/shared';
import { BulletRewritePanel } from '@/components/BulletRewritePanel';
import { Navigation } from '@/components/Navigation';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader } from '@/components/ui/card';

// ── Helpers ────────────────────────────────────────────────────────────────────

function scoreColor(score: number): string {
  if (score >= 75) return 'text-green-700';
  if (score >= 50) return 'text-yellow-700';
  return 'text-red-700';
}

/** Convert "YYYY-MM-DD" → "Mon YYYY". Returns value unchanged if it doesn't match. */
function formatIsoDate(value: string): string {
  const m = /^(\d{4})-(\d{2})/.exec(value);
  if (!m) return value;
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[parseInt(m[2]!, 10) - 1] ?? ''} ${m[1]!}`;
}

// ── Bullet item ────────────────────────────────────────────────────────────────

interface ResumeBulletItemProps {
  bullet: ResumeBullet;
  bulletRow?: ResumeBulletRow;
  resumeId?: string;
  onDelete?: (bulletId: string) => void;
  onUpdate?: (bulletId: string, text: string) => void;
}

function ResumeBulletItem({ bullet, bulletRow, resumeId, onDelete, onUpdate }: ResumeBulletItemProps) {
  const [showTip, setShowTip] = useState(false);
  const [showReqTip, setShowReqTip] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState(bullet.text);
  const [showRewrite, setShowRewrite] = useState(false);
  const [pendingAction, setPendingAction] = useState<'delete' | 'rewrite' | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const score = bullet.confidenceScore ?? (bulletRow?.confidenceScore ?? undefined);

  useEffect(() => {
    setEditText(bullet.text);
  }, [bullet.text]);

  useEffect(() => {
    if (editing) textareaRef.current?.focus();
  }, [editing]);

  const handleSaveEdit = () => {
    const trimmed = editText.trim();
    if (trimmed && trimmed !== bullet.text && bulletRow && onUpdate) {
      onUpdate(bulletRow.id, trimmed);
    }
    setEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && e.ctrlKey) handleSaveEdit();
    if (e.key === 'Escape') { setEditText(bullet.text); setEditing(false); }
  };

  const canEdit = !!(bulletRow && onDelete && onUpdate);

  return (
    <li className="flex gap-2 py-0.5 group">
      <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary/50" />
      <div className="flex-1 min-w-0">
        {editing ? (
          <div className="flex flex-col gap-1">
            <textarea
              ref={textareaRef}
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              onKeyDown={handleKeyDown}
              rows={3}
              className="w-full rounded border border-input bg-background px-2 py-1 text-sm leading-relaxed resize-none focus:outline-none focus:ring-1 focus:ring-primary"
            />
            <div className="flex gap-1.5">
              <button
                onClick={handleSaveEdit}
                className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs bg-primary text-primary-foreground hover:bg-primary/90"
              >
                <Check className="h-3 w-3" /> Save
              </button>
              <button
                onClick={() => { setEditText(bullet.text); setEditing(false); }}
                className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs border hover:bg-muted"
              >
                <X className="h-3 w-3" /> Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="flex items-start gap-1">
            <p className="flex-1 text-sm leading-relaxed">{bullet.text}</p>
            {(canEdit || score !== undefined || bullet.reasoning || bullet.targetRequirement) && (
              <div className={`flex shrink-0 flex-col items-end gap-0.5 transition-opacity ${showRewrite || pendingAction ? 'opacity-100' : 'opacity-40 group-hover:opacity-100'}`}>
                {canEdit && (
                  <div className="flex items-center gap-0.5">
                    {pendingAction ? (
                      <>
                        <span className="text-xs text-muted-foreground pr-0.5">
                          {pendingAction === 'delete' ? 'Remove bullet?' : 'Open rewrite?'}
                        </span>
                        <button
                          onClick={() => setPendingAction(null)}
                          title="Cancel"
                          className="rounded p-1 text-muted-foreground hover:text-foreground hover:bg-muted"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                        <button
                          onClick={() => {
                            if (pendingAction === 'delete' && bulletRow) onDelete(bulletRow.id);
                            if (pendingAction === 'rewrite') setShowRewrite(true);
                            setPendingAction(null);
                          }}
                          title="Confirm"
                          className="rounded p-1 text-primary hover:text-primary hover:bg-muted"
                        >
                          <Check className="h-3.5 w-3.5" />
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => setEditing(true)}
                          title="Edit"
                          className="rounded p-1 text-muted-foreground hover:text-foreground hover:bg-muted"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        <button
                          onClick={() => showRewrite ? setShowRewrite(false) : setPendingAction('rewrite')}
                          title={showRewrite ? 'Close rewrite' : 'Rewrite with AI'}
                          className="rounded p-1 text-muted-foreground hover:text-foreground hover:bg-muted"
                        >
                          <Sparkles className="h-3.5 w-3.5" />
                        </button>
                        <button
                          onClick={() => setPendingAction('delete')}
                          title="Remove from resume"
                          className="rounded p-1 text-muted-foreground hover:text-destructive hover:bg-muted"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </>
                    )}
                  </div>
                )}
                {bullet.reasoning && (
                  <div className="relative">
                    <button
                      onMouseEnter={() => setShowTip(true)}
                      onMouseLeave={() => setShowTip(false)}
                      onFocus={() => setShowTip(true)}
                      onBlur={() => setShowTip(false)}
                      className="inline-flex items-center gap-1 rounded p-1 text-xs text-muted-foreground hover:text-foreground hover:bg-muted focus:outline-none"
                    >
                      {score !== undefined && (
                        <span className={`font-medium tabular-nums ${score >= 0.75 ? 'text-green-600' : score >= 0.5 ? 'text-yellow-600' : 'text-muted-foreground'}`}>
                          {Math.round(score * 100)}%
                        </span>
                      )}
                      <Info className="h-3.5 w-3.5" />
                    </button>
                    {showTip && (
                      <div className="absolute bottom-full right-0 z-10 mb-1.5 w-72 rounded-md border bg-popover px-3 py-2 text-xs text-popover-foreground shadow-md">
                        <p className="font-semibold mb-0.5 text-primary">Why this was chosen:</p>
                        <p className="leading-relaxed text-muted-foreground">{bullet.reasoning}</p>
                      </div>
                    )}
                  </div>
                )}
                {bullet.targetRequirement && (
                  <div className="relative">
                    <button
                      onMouseEnter={() => setShowReqTip(true)}
                      onMouseLeave={() => setShowReqTip(false)}
                      onFocus={() => setShowReqTip(true)}
                      onBlur={() => setShowReqTip(false)}
                      title="View job requirement this bullet addresses"
                      className="inline-flex items-center gap-1 rounded p-1 text-xs text-muted-foreground hover:text-foreground hover:bg-muted focus:outline-none"
                    >
                      <Crosshair className="h-3.5 w-3.5" />
                    </button>
                    {showReqTip && (
                      <div className="absolute bottom-full right-0 z-10 mb-1.5 w-72 rounded-md border bg-popover px-3 py-2 text-xs text-popover-foreground shadow-md">
                        <p className="font-semibold mb-0.5 text-primary">Addresses:</p>
                        <p className="leading-relaxed text-muted-foreground">{bullet.targetRequirement}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Rewrite panel */}
        {showRewrite && bulletRow && resumeId && (
          <BulletRewritePanel
            capabilityId={bulletRow.capabilityId ?? ''}
            currentBody={bullet.text}
            resumeId={resumeId}
            resumeBulletId={bulletRow.id}
            onAccept={(_format, suggestion) => {
              if (onUpdate) onUpdate(bulletRow.id, suggestion);
              setShowRewrite(false);
            }}
            onDismiss={() => setShowRewrite(false)}
          />
        )}
      </div>
    </li>
  );
}

// ── Shared bullet row props ───────────────────────────────────────────────────

interface BulletRowContext {
  bulletRowMap: Map<string, ResumeBulletRow>;
  resumeId: string;
  onDeleteBullet?: (bulletId: string) => void;
  onUpdateBullet?: (bulletId: string, text: string) => void;
}

// ── Project bullets ───────────────────────────────────────────────────────────

interface ProjectBulletsProps extends Partial<BulletRowContext> {
  project: ResumeProjectItem;
}

function ProjectBullets({ project, bulletRowMap, resumeId, onDeleteBullet, onUpdateBullet }: ProjectBulletsProps) {
  if (project.bullets.length === 0) return null;
  return (
    <div className="mt-2">
      {project.project && (
        <p className="text-xs italic text-muted-foreground/70">{project.project}</p>
      )}
      <ul className="mt-1 space-y-1">
        {project.bullets.map((bullet, i) => (
          <ResumeBulletItem
            key={bullet.capabilityId ?? `${i}-${bullet.text.slice(0, 20)}`}
            bullet={bullet}
            bulletRow={bullet.capabilityId ? bulletRowMap?.get(bullet.capabilityId) : undefined}
            resumeId={resumeId}
            onDelete={onDeleteBullet}
            onUpdate={onUpdateBullet}
          />
        ))}
      </ul>
    </div>
  );
}

// ── Experience block ──────────────────────────────────────────────────────────

interface ExperienceBlockProps extends Partial<BulletRowContext> {
  item: ResumeExperienceItem;
  collapseProjects?: boolean;
}

function ExperienceBlock({ item, collapseProjects, bulletRowMap, resumeId, onDeleteBullet, onUpdateBullet }: ExperienceBlockProps) {
  // Defensive: legacy resumes generated before the projects[] schema change
  // (llm-output-redesign-plan §2.10) lack item.projects entirely. Skip them
  // rather than crash; the user will need to regenerate.
  const projects = item.projects ?? [];
  const hasAnyBullets = projects.some((p) => p.bullets.length > 0);
  if (!hasAnyBullets) return null;

  return (
    <div className="mb-6">
      <div className="mb-1">
        <h3 className="font-semibold text-sm">{item.title}</h3>
        <p className="text-sm text-muted-foreground">
          {item.company}
          {item.dateRange && <span className="ml-2 text-xs">· {item.dateRange}</span>}
        </p>
      </div>

      {collapseProjects ? (
        // Flat bullet list — no project labels
        <div className="mt-2">
          <ul className="mt-1 space-y-1">
            {projects.flatMap((project) =>
              project.bullets.map((bullet, i) => (
                <ResumeBulletItem
                  key={bullet.capabilityId ?? `${i}-${bullet.text.slice(0, 20)}`}
                  bullet={bullet}
                  bulletRow={bullet.capabilityId ? bulletRowMap?.get(bullet.capabilityId) : undefined}
                  resumeId={resumeId}
                  onDelete={onDeleteBullet}
                  onUpdate={onUpdateBullet}
                />
              ))
            )}
          </ul>
        </div>
      ) : (
        projects.map((project, projectIdx) => (
          <ProjectBullets
            key={`${item.titleId}-${projectIdx}`}
            project={project}
            bulletRowMap={bulletRowMap}
            resumeId={resumeId}
            onDeleteBullet={onDeleteBullet}
            onUpdateBullet={onUpdateBullet}
          />
        ))
      )}
    </div>
  );
}

// ── Requirements panel ────────────────────────────────────────────────────────

function ReqList({
  title,
  items,
  color = 'primary',
}: {
  title: string;
  items: string[];
  color?: string;
}) {
  if (!items || items.length === 0) return null;
  return (
    <div className="mb-5">
      <h4 className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {title}
      </h4>
      <ul className="space-y-1">
        {items.map((item) => (
          <li key={item} className="flex gap-2 text-sm">
            <span className={`mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-${color}/60`} />
            {item}
          </li>
        ))}
      </ul>
    </div>
  );
}

// ── Resume panel ──────────────────────────────────────────────────────────────

interface ResumePanelProps extends Partial<BulletRowContext> {
  document: ResumeDocument;
  viewPreferences: ViewPreferences;
}

function ResumePanel({
  document,
  viewPreferences,
  bulletRowMap,
  resumeId,
  onDeleteBullet,
  onUpdateBullet,
}: ResumePanelProps) {
  const { collapseTitles, collapseProjects } = viewPreferences;

  return (
    <div>
      {/* Header */}
      <div className="mb-4 border-b pb-4">
        <h2 className="text-xl font-bold">{document.header.name}</h2>
        {document.header.email && (
          <p className="text-sm text-muted-foreground">{document.header.email}</p>
        )}
        {document.header.location && (
          <p className="text-sm text-muted-foreground">{document.header.location}</p>
        )}
      </div>

      {/* Summary */}
      {document.summary && (
        <div className="mb-5">
          <h3 className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Professional Summary
          </h3>
          <p className="text-sm leading-relaxed">{document.summary}</p>
        </div>
      )}

      {/* Sections */}
      {document.sections.map((section, sectionIdx) => {
        if (section.type === 'experience') {
          // Group items by tenureId, preserving document order.
          // items[0] in each group is the most-recent title.
          const tenureOrder: string[] = [];
          const tenureGroups = new Map<
            string,
            { company: string; items: ResumeExperienceItem[] }
          >();

          section.items.forEach((item) => {
            if (!tenureGroups.has(item.tenureId)) {
              tenureOrder.push(item.tenureId);
              tenureGroups.set(item.tenureId, { company: item.company, items: [] });
            }
            tenureGroups.get(item.tenureId)!.items.push(item);
          });

          return (
            <div key={sectionIdx} className="mb-5">
              <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {section.title}
              </h3>

              {tenureOrder.map((tenureId) => {
                const group = tenureGroups.get(tenureId)!;

                if (collapseTitles && group.items.length > 1) {
                  // ── Titles collapsed: merge all projects under the most-recent title ──
                  const mostRecent = group.items[0]!;
                  const earliest = group.items[group.items.length - 1]!;
                  const mergedProjects = group.items.flatMap((i) => i.projects ?? []);

                  const rawStart = earliest.tenureStartDate ?? earliest.startDate;
                  const rawEnd = mostRecent.tenureEndDate ?? mostRecent.endDate;
                  const spanStart = rawStart ? formatIsoDate(rawStart) : null;
                  const spanEnd = rawEnd ? formatIsoDate(rawEnd) : 'Present';
                  const spanRange = spanStart
                    ? `${spanStart} – ${spanEnd}`
                    : (mostRecent.dateRange ?? null);

                  const mergedItem: ResumeExperienceItem = {
                    titleId: mostRecent.titleId,
                    tenureId: mostRecent.tenureId,
                    title: mostRecent.title,
                    company: mostRecent.company,
                    startDate: mostRecent.startDate,
                    endDate: mostRecent.endDate,
                    tenureStartDate: mostRecent.tenureStartDate,
                    tenureEndDate: mostRecent.tenureEndDate,
                    dateRange: spanRange ?? undefined,
                    projects: mergedProjects,
                  };

                  return (
                    <div key={tenureId} className="mb-4">
                      <ExperienceBlock
                        item={mergedItem}
                        collapseProjects={collapseProjects}
                        bulletRowMap={bulletRowMap}
                        resumeId={resumeId}
                        onDeleteBullet={onDeleteBullet}
                        onUpdateBullet={onUpdateBullet}
                      />
                    </div>
                  );
                }

                // ── Titles expanded: render each title separately ──
                return (
                  <div key={tenureId} className="mb-4">
                    {group.items.map((item, itemIdx) => (
                      <ExperienceBlock
                        key={item.titleId ?? `${tenureId}-${itemIdx}`}
                        item={item}
                        collapseProjects={collapseProjects}
                        bulletRowMap={bulletRowMap}
                        resumeId={resumeId}
                        onDeleteBullet={onDeleteBullet}
                        onUpdateBullet={onUpdateBullet}
                      />
                    ))}
                  </div>
                );
              })}
            </div>
          );
        }

        if (section.type === 'skills') {
          // If skillGroups exist, render grouped; otherwise render flat list (backward compat)
          const hasSkillGroups =
            'skillGroups' in document &&
            document.skillGroups &&
            document.skillGroups.length > 0;

          return (
            <div key={sectionIdx} className="mb-5">
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {section.title}
              </h3>

              {hasSkillGroups ? (
                <div className="space-y-3">
                  {'skillGroups' in document &&
                    document.skillGroups?.map((group, groupIdx) => (
                      <div key={group.name || groupIdx}>
                        {group.name && (
                          <p className="text-xs font-semibold text-foreground mb-1.5">
                            {group.name}
                          </p>
                        )}
                        <div className="flex flex-wrap gap-1.5">
                          {group.skills.map((skill) => (
                            <Badge key={skill} variant="secondary" className="text-xs">
                              {skill}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    ))}
                </div>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {section.items.map((skill) => (
                    <Badge key={skill} variant="secondary" className="text-xs">
                      {skill}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          );
        }

        if (section.type === 'education') {
          return (
            <div key={sectionIdx} className="mb-5">
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {section.title}
              </h3>
              <div className="space-y-2">
                {section.items.map((item) => {
                  const year = item.graduationDate
                    ? item.graduationDate.slice(0, 4)
                    : 'In Progress';
                  return (
                    <div key={item.id}>
                      <p className="text-sm font-semibold text-foreground">
                        {item.degreeAndMajor}{' '}
                        <span className="font-normal text-muted-foreground">({year})</span>
                      </p>
                      <p className="text-sm text-muted-foreground">{item.institution}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        }

        if (section.type === 'custom') {
          return (
            <div key={sectionIdx} className="mb-5">
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {section.title}
              </h3>
              <ul className="space-y-1">
                {section.bullets.map((bullet) => (
                  <li key={bullet.capabilityId} className="flex gap-2.5 py-0.5">
                    <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary/50" />
                    <p className="text-sm leading-relaxed">{bullet.text}</p>
                  </li>
                ))}
              </ul>
            </div>
          );
        }

        return null;
      })}
    </div>
  );
}

// ── Export button group ───────────────────────────────────────────────────────

const EXPORT_FORMATS: { format: ExportFormat; label: string }[] = [
  { format: 'md',   label: 'Markdown' },
  { format: 'pdf',  label: 'PDF' },
  { format: 'docx', label: 'Word' },
];

function ExportButtons({ resumeId }: { resumeId: string }) {
  const [loading, setLoading] = useState<ExportFormat | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function handleDownload(format: ExportFormat) {
    setLoading(format);
    setError(null);
    try {
      await resumeApi.download(resumeId, format);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Export failed');
    } finally {
      setLoading(null);
    }
  }

  return (
    <div className="flex items-center gap-1.5">
      <Download className="h-3.5 w-3.5 text-muted-foreground" />
      {EXPORT_FORMATS.map(({ format, label }) => (
        <button
          key={format}
          onClick={() => handleDownload(format)}
          disabled={loading !== null}
          className="inline-flex items-center gap-1 rounded border px-2 py-1 text-xs font-medium text-muted-foreground hover:border-primary/50 hover:text-foreground disabled:opacity-50 transition-colors"
        >
          {loading === format && <Loader2 className="h-3 w-3 animate-spin" />}
          {label}
        </button>
      ))}
      {error && <span className="ml-1 text-xs text-destructive">{error}</span>}
    </div>
  );
}

// ── ICP Scoring Table ─────────────────────────────────────────────────────────

interface IcpScoringTableProps {
  scoring: IcpScoringRow[];
  totalScore?: string;
  fitPercentage?: number;
}

function IcpScoringTable({ scoring, totalScore, fitPercentage }: IcpScoringTableProps) {
  if (!scoring || scoring.length === 0) return null;

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b">
            <th className="px-3 py-2 text-left font-semibold">#</th>
            <th className="px-3 py-2 text-left font-semibold">Criterion</th>
            <th className="px-3 py-2 text-center font-semibold">Weight</th>
            <th className="px-3 py-2 text-center font-semibold">Score</th>
            <th className="px-3 py-2 text-left font-semibold">Evidence</th>
          </tr>
        </thead>
        <tbody>
          {scoring.map((row) => (
            <tr key={row.criterionNumber} className="border-b hover:bg-muted/50">
              <td className="px-3 py-2 text-muted-foreground">{row.criterionNumber}</td>
              <td className="px-3 py-2">{row.criterion}</td>
              <td className="px-3 py-2 text-center font-medium">{row.weight}</td>
              <td className="px-3 py-2 text-center font-medium">{row.score} / 5</td>
              <td className="px-3 py-2 text-xs text-muted-foreground">{row.evidence}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {totalScore && fitPercentage !== undefined && (
        <div className="mt-4 pt-4 border-t flex items-center justify-between">
          <div className="text-sm font-semibold">Total Score</div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-muted-foreground">{totalScore}</span>
            <span
              className={`text-lg font-bold ${
                fitPercentage >= 75
                  ? 'text-green-600'
                  : fitPercentage >= 50
                  ? 'text-yellow-600'
                  : 'text-red-600'
              }`}
            >
              {fitPercentage}%
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Perspective Briefings ─────────────────────────────────────────────────────

interface PerspectiveBriefingsProps {
  briefings: PerspectiveBriefings;
}

function PerspectiveBriefingsDisplay({ briefings }: PerspectiveBriefingsProps) {
  const [expanded, setExpanded] = useState<'optimistic' | 'balanced' | 'critical' | null>(null);

  const perspectives = [
    { key: 'optimistic' as const, label: 'Optimistic View',       icon: '🟢', color: 'text-green-700' },
    { key: 'balanced'   as const, label: 'Balanced Assessment',   icon: '🟡', color: 'text-yellow-700' },
    { key: 'critical'   as const, label: 'Critical View',         icon: '🔴', color: 'text-red-700' },
  ];

  return (
    <div className="space-y-2">
      {perspectives.map(({ key, label, icon, color }) => (
        <div key={key} className="border rounded-md">
          <button
            onClick={() => setExpanded(expanded === key ? null : key)}
            className="w-full flex items-center justify-between px-4 py-3 hover:bg-muted/50 transition-colors"
          >
            <div className="flex items-center gap-2">
              <span className="text-lg">{icon}</span>
              <span className={`font-semibold ${color}`}>{label}</span>
            </div>
            <ChevronDown
              className={`h-4 w-4 transition-transform ${expanded === key ? 'rotate-180' : ''}`}
            />
          </button>
          {expanded === key && (
            <div className="px-4 py-3 border-t bg-muted/30 text-sm leading-relaxed text-muted-foreground">
              {briefings[key]}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function ResumeViewPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showIcpDetails, setShowIcpDetails] = useState(false);
  const [selectionMode, setSelectionMode] = useState(false);

  // Auth check
  const { data: auth, isLoading: authLoading } = useQuery({
    queryKey: ['me'],
    queryFn: authApi.me,
    retry: false,
  });

  useEffect(() => {
    if (!authLoading && !auth) navigate('/login');
  }, [auth, authLoading, navigate]);

  // Resume + polling while in-flight
  const { data: resume, isLoading: resumeLoading } = useQuery({
    queryKey: ['resume', id],
    queryFn: () => resumeApi.get(id!),
    enabled: !!auth && !!id,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === 'pending' || status === 'generating' ? 2000 : false;
    },
  });

  // Posting (for the requirements panel)
  const { data: posting } = useQuery({
    queryKey: ['posting', resume?.postingId],
    queryFn: () => jobPostingApi.getById(resume!.postingId),
    enabled: !!resume?.postingId,
  });

  // Resume bullets from the new table (new resumes only; empty for old resumes)
  const { data: bulletRows = [] } = useQuery({
    queryKey: ['resume-bullets', id],
    queryFn: () => resumeApi.getBullets(id!),
    enabled: !!auth && !!id && resume?.status === 'completed',
  });

  // Available capabilities for selection mode
  const { data: availableCapabilities = [] } = useQuery({
    queryKey: ['resume-available-caps', id],
    queryFn: () => resumeApi.getAvailableCapabilities(id!),
    enabled: selectionMode && !!id,
  });

  // Lookup map: capabilityId → bullet row (for matching document bullets to DB rows)
  const bulletRowMap = React.useMemo(
    () => new Map<string, ResumeBulletRow>(
      bulletRows.filter((r) => r.capabilityId).map((r) => [r.capabilityId!, r])
    ),
    [bulletRows]
  );

  // ── Mutations ──────────────────────────────────────────────────────────────

  const updateViewPrefsMutation = useMutation({
    mutationFn: (prefs: ViewPreferences) => authApi.updateViewPreferences(prefs),
    onSuccess: (updated) => {
      queryClient.setQueryData(['me'], updated);
    },
  });

  const invalidateBullets = () => {
    void queryClient.invalidateQueries({ queryKey: ['resume-bullets', id] });
    void queryClient.invalidateQueries({ queryKey: ['resume', id] });
    void queryClient.invalidateQueries({ queryKey: ['resume-available-caps', id] });
  };

  const updateBulletMutation = useMutation({
    mutationFn: ({ bulletId, text }: { bulletId: string; text: string }) =>
      resumeApi.updateBullet(id!, bulletId, text),
    onSuccess: invalidateBullets,
  });

  const deleteBulletMutation = useMutation({
    mutationFn: (bulletId: string) => resumeApi.deleteBullet(id!, bulletId),
    onSuccess: invalidateBullets,
  });

  const toggleBulletMutation = useMutation({
    mutationFn: (bulletId: string) => resumeApi.toggleBullet(id!, bulletId),
    onSuccess: invalidateBullets,
  });

  const addBulletMutation = useMutation({
    mutationFn: (capabilityId: string) => resumeApi.addBullet(id!, capabilityId),
    onSuccess: invalidateBullets,
  });

  // ── Handlers ───────────────────────────────────────────────────────────────

  function handleToggleViewPref(key: keyof ViewPreferences) {
    const current = auth?.user.viewPreferences ?? { collapseTitles: false, collapseProjects: false };
    updateViewPrefsMutation.mutate({ ...current, [key]: !current[key] });
  }

  function handleDeleteBullet(bulletId: string) {
    deleteBulletMutation.mutate(bulletId);
  }

  function handleUpdateBullet(bulletId: string, text: string) {
    updateBulletMutation.mutate({ bulletId, text });
  }

  function handleSelectionToggle(cap: AvailableCapability) {
    if (cap.isIncluded && cap.resumeBulletId) {
      toggleBulletMutation.mutate(cap.resumeBulletId);
    } else if (!cap.isIncluded) {
      addBulletMutation.mutate(cap.id);
    }
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  if (authLoading || resumeLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <div className="flex min-h-[calc(100vh-4rem)] items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  if (!resume) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <div className="flex min-h-[calc(100vh-4rem)] items-center justify-center">
          <p className="text-muted-foreground">Resume not found.</p>
        </div>
      </div>
    );
  }

  const req = posting?.requirements;
  const docTitle = resume.postingTitle ?? req?.role ?? 'Resume';
  const company = resume.postingCompany ?? '—';

  return (
    <div className="min-h-screen bg-background">
      <Navigation breadcrumb={docTitle} />

      <main className="mx-auto max-w-7xl px-4 py-8">
        {/* Toolbar: export buttons + original posting link */}
        {(resume.status === 'completed' || posting?.sourceUrl) && (
          <div className="mb-6 flex items-center justify-end gap-3 border-b pb-4">
            {resume.status === 'completed' && id && <ExportButtons resumeId={id} />}
            {posting?.sourceUrl && (
              <a
                href={posting.sourceUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
              >
                <ExternalLink className="h-3.5 w-3.5" />
                Original posting
              </a>
            )}
          </div>
        )}

        {/* Confidence summary + ICP score bar */}
        {resume.confidenceSummary && (
          <Card className="mb-6">
            <CardContent className="py-4">
              <div className="flex flex-wrap items-start gap-6">
                {resume.evaluation?.fitPercentage !== undefined && (
                  <div className="text-center">
                    <p className={`text-3xl font-bold ${scoreColor(resume.evaluation.fitPercentage)}`}>
                      {resume.evaluation.fitPercentage}
                      <span className="text-lg">%</span>
                    </p>
                    <p className="text-xs text-muted-foreground">Profile Match</p>
                  </div>
                )}

                <div className="flex-1 grid gap-4 sm:grid-cols-2">
                  {resume.confidenceSummary.strengths.length > 0 && (
                    <div>
                      <p className="mb-1.5 flex items-center gap-1 text-xs font-semibold text-green-700">
                        <CheckCircle2 className="h-3 w-3" /> Strengths
                      </p>
                      <ul className="space-y-1">
                        {resume.confidenceSummary.strengths.map((s) => (
                          <li key={s} className="text-xs text-muted-foreground">{s}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {resume.confidenceSummary.gaps.length > 0 && (
                    <div>
                      <p className="mb-1.5 flex items-center gap-1 text-xs font-semibold text-yellow-700">
                        <AlertTriangle className="h-3 w-3" /> Gaps
                      </p>
                      <ul className="space-y-1">
                        {resume.confidenceSummary.gaps.map((g) => (
                          <li key={g} className="text-xs text-muted-foreground">{g}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>

              {/* Collapsible ICP breakdown */}
              {resume.evaluation?.icpScoring && resume.evaluation.icpScoring.length > 0 && (
                <div className="mt-6 pt-6 border-t">
                  <button
                    onClick={() => setShowIcpDetails(!showIcpDetails)}
                    className="w-full flex items-center justify-between px-0 py-3 hover:bg-muted/50 transition-colors rounded"
                  >
                    <span className="flex items-center gap-2 text-sm font-semibold text-foreground">
                      <ChevronDown
                        className={`h-4 w-4 transition-transform ${showIcpDetails ? 'rotate-180' : ''}`}
                      />
                      Ideal Candidate Profile Breakdown
                    </span>
                  </button>
                  {showIcpDetails && (
                    <div className="px-0 py-3 text-sm">
                      <IcpScoringTable
                        scoring={resume.evaluation.icpScoring}
                        totalScore={resume.evaluation.totalScore}
                        fitPercentage={resume.evaluation.fitPercentage}
                      />
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Perspective Briefings */}
        {resume.evaluation?.perspectiveBriefings && (
          <Card className="mb-6">
            <CardHeader className="pb-3">
              <h3 className="text-base font-semibold">Recruiter Perspectives</h3>
              <p className="text-xs text-muted-foreground mt-1">
                How different hiring managers might view your resume
              </p>
            </CardHeader>
            <CardContent>
              <PerspectiveBriefingsDisplay briefings={resume.evaluation.perspectiveBriefings} />
            </CardContent>
          </Card>
        )}

        {/* In-flight state */}
        {(resume.status === 'pending' || resume.status === 'generating') && (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <Loader2 className="mb-4 h-10 w-10 animate-spin text-primary" />
            <h2 className="text-lg font-semibold">
              {resume.status === 'pending' ? 'Resume queued…' : 'Generating your resume…'}
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              The AI is matching your capabilities to the job requirements. This usually takes 15–30 seconds.
            </p>
          </div>
        )}

        {/* Failed state */}
        {resume.status === 'failed' && (
          <div className="rounded-md bg-destructive/10 p-6 text-center">
            <AlertTriangle className="mx-auto mb-3 h-8 w-8 text-destructive" />
            <h2 className="font-semibold text-destructive">Generation failed</h2>
            {resume.errorMessage && (
              <p className="mt-1 text-sm text-muted-foreground">{resume.errorMessage}</p>
            )}
          </div>
        )}

        {/* Side-by-side view */}
        {resume.status === 'completed' && resume.document && (
          <div className="grid gap-6 lg:grid-cols-[2fr_3fr]">
            {/* Left: requirements */}
            <div className="space-y-0">
              <div className="mb-4">
                <h2 className="text-base font-semibold">{docTitle}</h2>
                <p className="text-sm text-muted-foreground">
                  {company}
                  {posting?.location && ` · ${posting.location}`}
                </p>
                {req?.seniority && (
                  <Badge variant="secondary" className="mt-1.5 text-xs">
                    {req.seniority}
                  </Badge>
                )}
              </div>

              {req?.summary && (
                <div className="mb-5 rounded-md bg-muted/50 p-3 text-sm text-muted-foreground leading-relaxed">
                  {req.summary}
                </div>
              )}

              <ReqList title="Must Have"        items={req?.mustHaves      ?? []} />
              <ReqList title="Nice to Have"     items={req?.niceToHaves    ?? []} />
              <ReqList title="Responsibilities" items={req?.responsibilities ?? []} />

              {(posting?.keywords ?? []).length > 0 && (
                <div className="mt-4">
                  <h4 className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Keywords
                  </h4>
                  <div className="flex flex-wrap gap-1">
                    {(posting?.keywords ?? []).map((kw) => (
                      <Badge key={kw} variant="outline" className="text-xs">
                        {kw}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Right: generated resume */}
            <div className="rounded-lg border bg-card p-6 shadow-sm">
              {/* Toolbar: view preference toggles + selection mode */}
              <div className="mb-4 flex items-center justify-between border-b pb-3">
                <div className="flex items-center gap-1">
                  {([
                    { key: 'collapseTitles' as const, label: 'Titles' },
                    { key: 'collapseProjects' as const, label: 'Projects' },
                  ]).map(({ key, label }) => {
                    const active = auth?.user.viewPreferences?.[key] ?? false;
                    return (
                      <button
                        key={key}
                        onClick={() => handleToggleViewPref(key)}
                        disabled={updateViewPrefsMutation.isPending}
                        className={`inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs transition-colors disabled:opacity-50 ${
                          active
                            ? 'bg-muted text-foreground font-medium'
                            : 'text-muted-foreground hover:text-foreground hover:bg-muted/60'
                        }`}
                        title={active ? `Expand ${label.toLowerCase()}` : `Collapse ${label.toLowerCase()}`}
                      >
                        {active ? `${label} collapsed` : `Collapse ${label.toLowerCase()}`}
                      </button>
                    );
                  })}
                </div>
                {bulletRows.length > 0 && (
                  <button
                    onClick={() => setSelectionMode((v) => !v)}
                    className={`inline-flex items-center gap-1.5 rounded px-3 py-1.5 text-xs font-medium transition-colors ${
                      selectionMode
                        ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                        : 'border hover:bg-muted'
                    }`}
                  >
                    <ListChecks className="h-3.5 w-3.5" />
                    {selectionMode ? 'Done Selecting' : 'Select Bullets'}
                  </button>
                )}
              </div>

              {selectionMode ? (
                <SelectionPanel
                  capabilities={availableCapabilities}
                  onToggle={handleSelectionToggle}
                  isPending={toggleBulletMutation.isPending || addBulletMutation.isPending}
                />
              ) : (
                <ResumePanel
                  document={resume.document}
                  viewPreferences={auth?.user.viewPreferences ?? { collapseTitles: false, collapseProjects: false }}
                  bulletRowMap={bulletRows.length > 0 ? bulletRowMap : undefined}
                  resumeId={id}
                  onDeleteBullet={bulletRows.length > 0 ? handleDeleteBullet : undefined}
                  onUpdateBullet={bulletRows.length > 0 ? handleUpdateBullet : undefined}
                />
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

// ── Selection panel ────────────────────────────────────────────────────────────
// Shows all user capabilities with checkboxes, grouped by tenure → title → experience.

interface SelectionPanelProps {
  capabilities: AvailableCapability[];
  onToggle: (cap: AvailableCapability) => void;
  isPending: boolean;
}

function SelectionPanel({ capabilities, onToggle, isPending }: SelectionPanelProps) {
  if (capabilities.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <p className="text-sm text-muted-foreground">No capabilities found.</p>
      </div>
    );
  }

  // Group by tenureId → titleId → experienceId
  const tenureOrder: string[] = [];
  const tenureGroups = new Map<string, {
    company: string;
    titles: Map<string, {
      title: string;
      titleId: string;
      experiences: Map<string, { experienceId: string; project: string | null; caps: AvailableCapability[] }>;
    }>;
  }>();

  for (const cap of capabilities) {
    if (!tenureGroups.has(cap.tenureId)) {
      tenureOrder.push(cap.tenureId);
      tenureGroups.set(cap.tenureId, { company: cap.company, titles: new Map() });
    }
    const tg = tenureGroups.get(cap.tenureId)!;
    if (!tg.titles.has(cap.titleId)) {
      tg.titles.set(cap.titleId, { title: cap.title, titleId: cap.titleId, experiences: new Map() });
    }
    const titleGroup = tg.titles.get(cap.titleId)!;
    if (!titleGroup.experiences.has(cap.experienceId)) {
      titleGroup.experiences.set(cap.experienceId, {
        experienceId: cap.experienceId,
        project: cap.project,
        caps: [],
      });
    }
    titleGroup.experiences.get(cap.experienceId)!.caps.push(cap);
  }

  return (
    <div className="space-y-4">
      <p className="text-xs text-muted-foreground mb-3">
        Check to include a bullet on this resume. Uncheck to remove it.
      </p>
      {tenureOrder.map((tenureId) => {
        const tg = tenureGroups.get(tenureId)!;
        return (
          <div key={tenureId}>
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
              {tg.company}
            </p>
            {Array.from(tg.titles.values()).map((titleGroup) => (
              <div key={titleGroup.titleId} className="mb-3">
                <p className="text-sm font-semibold mb-1">{titleGroup.title}</p>
                {Array.from(titleGroup.experiences.values()).map((expGroup) => (
                  <div key={expGroup.experienceId} className="mb-2 ml-3">
                    {expGroup.project && (
                      <p className="text-xs italic text-muted-foreground/70 mb-1">{expGroup.project}</p>
                    )}
                    <ul className="space-y-1">
                      {expGroup.caps.map((cap) => (
                        <li key={cap.id} className="flex items-start gap-2">
                          <button
                            onClick={() => onToggle(cap)}
                            disabled={isPending}
                            className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors disabled:opacity-50 ${
                              cap.isIncluded
                                ? 'bg-primary border-primary text-primary-foreground'
                                : 'border-input hover:border-primary'
                            }`}
                          >
                            {cap.isIncluded && <Check className="h-3 w-3" />}
                          </button>
                          <span className="text-sm leading-relaxed">{cap.body}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}
